import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { useGridFilterDisplay } from 'ag-grid-react';
import type { CustomFilterProps } from 'ag-grid-react';
import type { GridApi, IAfterGuiAttachedParams } from 'ag-grid-community';
import { produce } from 'immer';
import { FormattedMessage, useIntl } from 'react-intl';
import { debounce } from '@mui/material/utils';

import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import type { SelectChangeEvent } from '@mui/material/Select';

import { AgGridFilterDropdown } from '@/components/AgGrid/components/CustomHeaderFilter/CustomSetColumnFilter/StyledComponents';
import { messages } from '@/components/AgGrid/helpers/translations';
import Tooltip from '@/components/mui/Tooltip';
import { isEllipsisActive } from '@/components/AgGrid/helpers';
import type {
  BankFeedAutocompleteOption,
  BankFeedSetFilterModel,
} from '@/domain/bankFeed/schemas';

type FilterType = BankFeedSetFilterModel['type'];

// Data-loading contract injected via `filterParams`. Keeps this filter decoupled
// from any specific API: the consumer supplies a stable `loadOptions` that fetches
// the option list, reading any dynamic deps (currency, accountType, …) fresh from
// the grid `api` at call time. The reference MUST be stable — a new identity each
// render would change `columnDefs` and re-render every filter, firing a fetch per
// column on unrelated state changes (e.g. a currency switch).
export type SetColumnFilterParams = {
  loadOptions: (args: { field: string; api: GridApi }) => Promise<BankFeedAutocompleteOption[]>;
};

type Props = CustomFilterProps<unknown, unknown, BankFeedSetFilterModel> & SetColumnFilterParams;

// BE convention: a synthetic "(Blanks)" option groups rows with an empty value.
// Hidden while a search query is active — it never matches a textual query.
const BLANK_ID = '_blank';

const sameIdSet = (
  a: ReadonlyArray<BankFeedAutocompleteOption>,
  b: ReadonlyArray<BankFeedAutocompleteOption>,
): boolean => {
  if (a.length !== b.length) return false;
  const idsA = new Set(a.map((o) => o.id));
  return b.every((o) => idsA.has(o.id));
};

type FilterItemProps = {
  option: BankFeedAutocompleteOption;
  checked: boolean;
  onToggle: (option: BankFeedAutocompleteOption) => void;
};

const FilterItem = ({ option, checked, onToggle }: FilterItemProps) => {
  const labelRef = useRef<HTMLDivElement | null>(null);
  const [isOverflowed, setIsOverflowed] = useState(false);

  const handleMouseEnter = useCallback(() => {
    setIsOverflowed(isEllipsisActive(labelRef.current));
  }, []);

  return (
    <Box
      role="presentation"
      className="ag-set-filter-item"
      onClick={() => onToggle(option)}
      sx={{ minHeight: (t) => t.spacing(4), display: 'flex', alignItems: 'center', cursor: 'pointer' }}
    >
      <Tooltip t={option.label} disableHoverListener={!isOverflowed} placement="right">
        <div className="ag-set-filter-item-checkbox ag-labeled ag-label-align-right ag-checkbox ag-input-field">
          <div
            ref={labelRef}
            onMouseEnter={handleMouseEnter}
            className="ag-input-field-label ag-label ag-checkbox-label ag-label-ellipsis"
          >
            {option.label}
          </div>
          <div
            className={`ag-wrapper ag-input-wrapper ag-checkbox-input-wrapper${checked ? ' ag-checked' : ''}`}
            role="presentation"
          >
            <input
              className="ag-input-field-input ag-checkbox-input"
              type="checkbox"
              tabIndex={-1}
              checked={checked}
              readOnly
            />
          </div>
        </div>
      </Tooltip>
    </Box>
  );
};

const BankFeedSetColumnFilter = (props: Props) => {
  const { model, onModelChange, colDef, api, loadOptions } = props;
  const { formatMessage } = useIntl();
  const { field } = colDef;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  // Options are fetched lazily on each popup open (see `afterGuiAttached`), not
  // eagerly on render: the grid keeps the filter mounted between opens and the
  // grid `context` is an "Initial" option (not reactive), so a render-time read of
  // dynamic deps would be stale. Fetching on open reads the latest deps and only
  // hits the network for the column the user actually opens.
  const [options, setOptions] = useState<BankFeedAutocompleteOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // Guards against a stale response overwriting a newer one if the deps change
  // (e.g. currency) and the user reopens before the previous fetch resolves.
  const requestIdRef = useRef(0);
  // The search input is disabled while loading, so focusing it inside
  // `afterGuiAttached` (isLoading just set true) is a no-op. Defer the focus to
  // when the on-open fetch resolves and the input is re-enabled.
  const shouldFocusOnLoadRef = useRef(false);

  // `draftSelection` is the user's working override; `null` means "no edits yet —
  // selected derives purely from applied model or default-all-selected". This
  // sidesteps any synchronisation effects: derivation happens at read time.
  const [draftSelection, setDraftSelection] = useState<BankFeedAutocompleteOption[] | null>(null);
  const [filterType, setFilterType] = useState<FilterType>(model?.type ?? 'contains');
  const [searchValue, setSearchValue] = useState('');
  const [closeFilter, setCloseFilter] = useState<(() => void) | undefined>(undefined);

  const derivedSelection = useMemo<BankFeedAutocompleteOption[]>(
    () => (model?.values?.length ? model.values : options),
    [model, options],
  );

  const selected = draftSelection ?? derivedSelection;

  // Fetch options + reset working state on each popup open. `loadOptions` reads the
  // latest deps fresh from `api`, so a kept-mounted filter still gets the current
  // currency/accountType. searchValue is intentionally preserved across reopens
  // (workspace parity).
  const afterGuiAttached = useCallback(
    (params?: IAfterGuiAttachedParams) => {
      const requestId = (requestIdRef.current += 1);
      setIsLoading(true);
      loadOptions({ field: field ?? '', api })
        .then((opts) => {
          if (requestIdRef.current !== requestId) return;
          setOptions(opts);
          setIsLoading(false);
        })
        .catch(() => {
          if (requestIdRef.current !== requestId) return;
          setOptions([]);
          setIsLoading(false);
        });

      setFilterType(model?.type ?? 'contains');
      setDraftSelection(null);
      if (params?.hidePopup) {
        setCloseFilter(() => params.hidePopup);
      }
      shouldFocusOnLoadRef.current = !params?.suppressFocus;
    },
    [loadOptions, field, api, model],
  );

  // Filter handlers are on for every grid: the grid keeps the model, this component only edits it.
  useGridFilterDisplay({ afterGuiAttached });

  // Focus the search input once the on-open fetch resolves. The input is disabled
  // while loading, so focusing inside `afterGuiAttached` (isLoading just set true)
  // would no-op; this effect runs after the isLoading=false commit, when the input
  // is re-enabled.
  useEffect(() => {
    if (!isLoading && shouldFocusOnLoadRef.current) {
      searchInputRef.current?.focus();
      shouldFocusOnLoadRef.current = false;
    }
  }, [isLoading]);

  const handleSearchInput = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  }, []);

  const debouncedHandleSearchInput = useMemo(
    () => debounce(handleSearchInput, 250),
    [handleSearchInput],
  );

  // Cancel any pending debounce timer when the component unmounts so a delayed
  // `setSearchValue` does not fire on an already-unmounted component.
  useEffect(() => () => debouncedHandleSearchInput.clear(), [debouncedHandleSearchInput]);

  const handleFilterTypeChange = useCallback((event: SelectChangeEvent<unknown>) => {
    const value = event.target.value;
    if (value === 'contains' || value === 'notContains') {
      setFilterType(value);
    }
  }, []);

  const filteredOptions = useMemo(() => {
    const search = searchValue.trim().toLowerCase();
    if (search.length === 0) return options;
    return options
      .filter((opt) => opt.id !== BLANK_ID)
      .filter((opt) => opt.label.toLowerCase().includes(search));
  }, [options, searchValue]);

  // Selected items visible under the current search — committed instead of the
  // full `selected` when Apply runs with an active search query.
  const visibleSelected = useMemo(() => {
    if (searchValue.length === 0) return selected;
    const filteredIds = new Set(filteredOptions.map((o) => o.id));
    return selected.filter((s) => filteredIds.has(s.id));
  }, [searchValue, selected, filteredOptions]);

  const allFilteredSelected = useMemo(() => {
    if (filteredOptions.length === 0) return false;
    const selectedIds = new Set(selected.map((s) => s.id));
    return filteredOptions.every((o) => selectedIds.has(o.id));
  }, [filteredOptions, selected]);

  // Every option in the fetched list is selected — treated as "no filter" on Apply.
  const allPossibleSelected = useMemo(() => {
    if (options.length === 0) return false;
    const selectedIds = new Set(selected.map((s) => s.id));
    return options.every((o) => selectedIds.has(o.id));
  }, [options, selected]);

  const handleSelectAll = useCallback(() => {
    const base = selected;
    if (allFilteredSelected) {
      const filteredIds = new Set(filteredOptions.map((o) => o.id));
      setDraftSelection(base.filter((s) => !filteredIds.has(s.id)));
      return;
    }
    const baseIds = new Set(base.map((b) => b.id));
    setDraftSelection([...base, ...filteredOptions.filter((o) => !baseIds.has(o.id))]);
  }, [selected, allFilteredSelected, filteredOptions]);

  const handleToggle = useCallback(
    (option: BankFeedAutocompleteOption) => {
      setDraftSelection(
        produce(selected, (draft) => {
          const idx = draft.findIndex((s) => s.id === option.id);
          if (idx === -1) draft.push(option);
          else draft.splice(idx, 1);
        }),
      );
    },
    [selected],
  );

  // Apply is disabled when a search is active and no currently-visible items are
  // selected — committing would either be a no-op or wipe an unrelated selection.
  const applyDisabled = searchValue.length > 0 && visibleSelected.length === 0;

  const hadActiveFilter = (model?.values.length ?? 0) > 0;

  const handleApply = useCallback(() => {
    if (applyDisabled) return;

    const valuesToCommit = searchValue.length > 0 ? visibleSelected : selected;

    // "All possible selected" without search → no-op semantically; clear committed
    // filter if one was present, otherwise do nothing.
    if (allPossibleSelected && searchValue.length === 0) {
      if (hadActiveFilter) onModelChange(null);
      closeFilter?.();
      return;
    }

    // Nothing to commit — clear an existing filter or do nothing if there wasn't one.
    if (valuesToCommit.length === 0) {
      if (hadActiveFilter) onModelChange(null);
      closeFilter?.();
      return;
    }

    // Dirty-check: skip emitting an identical model (avoids redundant grid refetch).
    if (model && model.type === filterType && sameIdSet(model.values, valuesToCommit)) {
      closeFilter?.();
      return;
    }

    onModelChange({ filterType: 'set', type: filterType, values: valuesToCommit });
    closeFilter?.();
  }, [
    applyDisabled,
    searchValue,
    visibleSelected,
    selected,
    allPossibleSelected,
    hadActiveFilter,
    model,
    filterType,
    onModelChange,
    closeFilter,
  ]);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      handleApply();
    },
    [handleApply],
  );

  const handleCancel = useCallback(() => {
    closeFilter?.();
  }, [closeFilter]);

  return (
    <form className="ag-filter-wrapper ag-focus-managed" onSubmit={handleSubmit}>
      <div className="ag-filter-body-wrapper ag-set-filter-body-wrapper">
        <Box ref={containerRef} className="ag-set-filter" sx={{ m: '10px' }}>
          <AgGridFilterDropdown
            value={filterType}
            onChange={handleFilterTypeChange}
            MenuProps={{ container: containerRef.current, disablePortal: true }}
          >
            <MenuItem value="contains">
              <FormattedMessage {...messages.contains} />
            </MenuItem>
            <MenuItem value="notContains">
              <FormattedMessage {...messages.notContains} />
            </MenuItem>
          </AgGridFilterDropdown>
        </Box>
        <div className="ag-set-filter">
          <Box
            role="presentation"
            className="ag-labeled ag-label-align-left ag-text-field ag-input-field"
            sx={{ mx: '10px', mb: 1 }}
          >
            <div
              role="presentation"
              className="ag-input-field-label ag-label ag-hidden ag-text-field-label"
            />
            <div role="presentation" className="ag-wrapper ag-input-wrapper ag-text-field-input-wrapper">
              <div
                role="presentation"
                onClick={isLoading ? undefined : handleSelectAll}
                className={`ag-wrapper ag-input-wrapper ag-checkbox-input-wrapper${allFilteredSelected ? ' ag-checked' : ''}${isLoading ? ' ag-disabled' : ''}`}
              >
                <input
                  type="checkbox"
                  tabIndex={-1}
                  checked={allFilteredSelected}
                  readOnly
                  disabled={isLoading}
                  className="ag-input-field-input ag-checkbox-input"
                />
              </div>
              <input
                ref={searchInputRef}
                type="text"
                tabIndex={0}
                disabled={isLoading}
                className="ag-input-field-input ag-text-field-input"
                placeholder={formatMessage(messages.searchOoo)}
                onChange={debouncedHandleSearchInput}
              />
            </div>
          </Box>

          {isLoading && <LinearProgress sx={{ my: 2, mx: 1.5 }} />}

          {!isLoading && filteredOptions.length === 0 && (
            <Box textAlign="center" sx={{ p: 2 }}>
              {searchValue.length > 0 ? (
                <FormattedMessage {...messages.noMatches} />
              ) : (
                <FormattedMessage {...messages.noOptions} />
              )}
            </Box>
          )}

          {/* Scroll once the list exceeds the reference workspace cap of ~7 rows × 32px. */}
          <Box sx={{ maxHeight: (t) => t.spacing(28), overflowY: 'auto' }}>
            {!isLoading &&
              filteredOptions.map((option) => (
                <FilterItem
                  key={option.id}
                  option={option}
                  checked={selected.some((s) => s.id === option.id)}
                  onToggle={handleToggle}
                />
              ))}
          </Box>
        </div>
      </div>

      <div className="ag-filter-apply-panel">
        <button
          type="submit"
          className="ag-button ag-standard-button ag-filter-apply-panel-button"
          disabled={applyDisabled}
        >
          {formatMessage(messages.applyFilter)}
        </button>
        <button
          type="button"
          className="ag-button ag-standard-button ag-filter-apply-panel-button"
          onClick={handleCancel}
        >
          {formatMessage(messages.cancelFilter)}
        </button>
      </div>
    </form>
  );
};

export default BankFeedSetColumnFilter;
